/**
 * Financial administration.
 *
 * Two ideas shape this screen: an invoice is only confirmed when somebody can
 * point at the money (a signed webhook, or an administrator recording a real
 * transfer), and a discrepancy is never auto-corrected — it is flagged,
 * explained and cleared by a human.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { apiErrorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/utils';
import { PAYMENT_KEY, PAYOUT_KEY, paymentTone, payoutTone, railTone } from '../../lib/contest';
import type { LedgerEntry, Payment, Payout, RailBalance } from '../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FullPageLoader } from '../../components/ui/spinner';

interface LedgerPayload {
  entries: LedgerEntry[];
  balances: RailBalance[];
}

export default function AdminFinancePage() {
  const { t, locale } = useTranslation();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [flagged, setFlagged] = useState<Payment[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [ledger, setLedger] = useState<LedgerPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [settleAmount, setSettleAmount] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pending, flaggedData, payoutData, ledgerData] = await Promise.all([
        api.get<{ payments: Payment[] }>('/admin/payments?status=pending&limit=100'),
        api.get<{ payments: Payment[] }>('/admin/payments?reconciliation=1&limit=100'),
        api.get<{ payouts: Payout[] }>('/admin/payouts?limit=100'),
        api.get<LedgerPayload>('/admin/ledger?limit=50'),
      ]);
      setPayments(pending.payments);
      setFlagged(flaggedData.payments);
      setPayouts(payoutData.payouts);
      setLedger(ledgerData);
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(id: string, path: string, body?: unknown): Promise<void> {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      await api.post(path, body);
      setNotice(t('admin.contests.transitionDone'));
      await load();
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">
          {t('admin.payments.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">{t('admin.payments.subtitle')}</p>
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      {notice && !error && (
        <div className="mt-6">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t('admin.payments.flagged')}</CardTitle>
        </CardHeader>
        <CardContent>
          {flagged.length === 0 ? (
            <p className="text-sm text-muted">{t('common.none')}</p>
          ) : (
            <ul className="flex flex-col gap-4">
              {flagged.map((payment) => (
                <li key={payment.id} className="flex flex-col gap-2 border-b border-border pb-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge tone={railTone(payment.rail)}>{payment.rail}</Badge>
                    <span className="text-fg">{payment.email ?? payment.userId}</span>
                    <span className="text-muted">{payment.contestTitle}</span>
                    <span className="ms-auto font-medium text-fg">
                      {payment.amount} {payment.asset}
                    </span>
                    <span className="text-muted">
                      {t('admin.payments.settleAmount')}: {payment.amountReceived}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                      <Label htmlFor={`note-${payment.id}`}>{t('admin.payments.resolveNote')}</Label>
                      <Input
                        id={`note-${payment.id}`}
                        value={notes[payment.id] ?? ''}
                        onChange={(event) =>
                          setNotes((previous) => ({
                            ...previous,
                            [payment.id]: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      loading={busyId === payment.id}
                      onClick={() =>
                        void act(
                          payment.id,
                          `/admin/payments/${payment.id}/resolve-reconciliation`,
                          { note: notes[payment.id] ?? '' },
                        )
                      }
                    >
                      {t('admin.payments.resolve')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t('admin.payments.entry')}</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted">{t('common.none')}</p>
          ) : (
            <ul className="flex flex-col gap-5">
              {payments.map((payment) => (
                <li key={payment.id} className="flex flex-col gap-2 border-b border-border pb-4">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge tone={railTone(payment.rail)}>{payment.rail}</Badge>
                    <Badge tone={paymentTone(payment.status)}>
                      {t(PAYMENT_KEY[payment.status])}
                    </Badge>
                    <span className="text-fg">{payment.email ?? payment.userId}</span>
                    <span className="text-muted">{payment.contestTitle}</span>
                    <span className="ms-auto font-medium text-fg">
                      {payment.amount} {payment.asset}
                    </span>
                    <span className="text-xs text-muted">{payment.providerMode}</span>
                  </div>

                  {payment.providerReference && (
                    <p className="text-xs text-muted">
                      {t('admin.payments.reference')}: {payment.providerReference}
                    </p>
                  )}

                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                      <Label htmlFor={`amount-${payment.id}`}>
                        {t('admin.payments.settleAmount')}
                      </Label>
                      <Input
                        id={`amount-${payment.id}`}
                        inputMode="decimal"
                        value={settleAmount[payment.id] ?? payment.amount}
                        onChange={(event) =>
                          setSettleAmount((previous) => ({
                            ...previous,
                            [payment.id]: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      loading={busyId === payment.id}
                      onClick={() =>
                        void act(payment.id, `/admin/payments/${payment.id}/settle`, {
                          amount: settleAmount[payment.id] ?? payment.amount,
                        })
                      }
                    >
                      {t('admin.payments.settle')}
                    </Button>
                    <Button
                      variant="ghost"
                      loading={busyId === payment.id}
                      onClick={() => void act(payment.id, `/admin/payments/${payment.id}/expire`)}
                    >
                      {t('admin.payments.expire')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t('admin.payouts.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-sm text-muted">{t('admin.payouts.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-4">
              {payouts.map((payout) => (
                <li
                  key={payout.id}
                  className="flex flex-wrap items-center gap-2 border-b border-border pb-3 text-sm"
                >
                  <Badge tone={railTone(payout.rail)}>{payout.rail}</Badge>
                  <Badge tone={payoutTone(payout.status)}>{t(PAYOUT_KEY[payout.status])}</Badge>
                  <span className="text-fg">
                    {t(payout.kind === 'prize' ? 'payout.kind.prize' : 'payout.kind.refund')}
                  </span>
                  <span className="text-muted">{payout.email ?? payout.userId}</span>
                  <span className="font-medium text-fg">
                    {payout.amount} {payout.asset}
                  </span>

                  <span className="ms-auto flex flex-wrap gap-2">
                    {payout.status === 'pending' && (
                      <Button
                        size="sm"
                        loading={busyId === payout.id}
                        onClick={() => void act(payout.id, `/admin/payouts/${payout.id}/approve`)}
                      >
                        {t('admin.payouts.approve')}
                      </Button>
                    )}
                    {payout.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        loading={busyId === payout.id}
                        onClick={() => void act(payout.id, `/admin/payouts/${payout.id}/process`)}
                      >
                        {t('admin.payouts.process')}
                      </Button>
                    )}
                    {(payout.status === 'approved' || payout.status === 'processing') && (
                      <Button
                        size="sm"
                        loading={busyId === payout.id}
                        onClick={() => void act(payout.id, `/admin/payouts/${payout.id}/complete`)}
                      >
                        {t('admin.payouts.complete')}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {ledger && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('admin.ledger.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-4">
              {ledger.balances.map((balance) => (
                <div key={`${balance.rail}-${balance.asset}`} className="text-sm">
                  <Badge tone={railTone(balance.rail)}>{balance.rail}</Badge>
                  <p className="mt-1 font-medium text-fg">
                    {balance.asset}: {balance.net}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted">{t('admin.ledger.note')}</p>

            <ul className="flex flex-col gap-2 text-sm">
              {ledger.entries.slice(0, 25).map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center gap-2">
                  <Badge tone={railTone(entry.rail)}>{entry.rail}</Badge>
                  <span className="text-muted">{entry.entry_type}</span>
                  <span className="text-fg">{entry.direction}</span>
                  <span className="ms-auto text-fg">
                    {entry.amount_minor} {entry.asset}
                  </span>
                  <span className="text-xs text-muted">
                    {formatDateTime(entry.created_at, locale)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
